import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Container,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { termsApi, TermsCurrentResponse } from '../services/api';
import { colors } from '../theme/tokens';

const Terms: React.FC = () => {
  const navigate = useNavigate();
  const { updateToken, hasAcceptedTerms, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [termsData, setTermsData] = useState<TermsCurrentResponse | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Redirect if user has already accepted terms
  useEffect(() => {
    if (!authLoading && hasAcceptedTerms()) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasAcceptedTerms, navigate]);

  useEffect(() => {
    // Fetch current terms
    const fetchTerms = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await termsApi.getCurrentTerms();
        setTermsData(response.data);
      } catch (err: any) {
        console.error('Failed to fetch terms:', err);
        setError(
          err.response?.data?.message ||
          'Failed to load terms and conditions. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // Check if user has scrolled to bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !termsData) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      setShowScrollIndicator(!isAtBottom);
    };

    container.addEventListener('scroll', checkScroll);
    checkScroll(); // Initial check

    return () => {
      container.removeEventListener('scroll', checkScroll);
    };
  }, [termsData]);

  const handleAccept = async () => {
    if (!agreed) return;

    try {
      setAccepting(true);
      setError(null);
      const response = await termsApi.acceptTerms();
      
      // Update token with the new token from response
      updateToken(response.data.token);
      
      // Redirect to dashboard
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Failed to accept terms:', err);
      setError(
        err.response?.data?.message ||
        'Failed to accept terms and conditions. Please try again.'
      );
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
            Loading terms and conditions...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !termsData) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Error
            </Typography>
            <Typography>{error}</Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Terms and Conditions
          </Typography>
          
          {termsData && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Version: {termsData.currentVersion}
            </Typography>
          )}

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Scrollable terms text container */}
          <Box
            ref={scrollContainerRef}
            sx={{
              flex: 1,
              maxHeight: '60vh',
              overflowY: 'auto',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 3,
              backgroundColor: 'background.default',
              position: 'relative',
            }}
          >
            {termsData && (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
              >
                {termsData.termsText}
              </Typography>
            )}

            {/* Scroll indicator */}
            {showScrollIndicator && (
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: `linear-gradient(to top, ${colors.background}f2, transparent)`,
                  py: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Please scroll to the bottom to continue
                </Typography>
              </Box>
            )}
          </Box>

          {/* Agreement checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={accepting}
              />
            }
            label={
              <Typography variant="body1">
                I have read and agree to the Terms and Conditions
              </Typography>
            }
            sx={{ mb: 3 }}
          />

          {/* Accept button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleAccept}
            disabled={!agreed || accepting}
            sx={{ py: 1.5 }}
          >
            {accepting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Accepting...
              </>
            ) : (
              'Accept Terms and Conditions'
            )}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Terms;

